import { supabase } from './supabaseClient';
import { withSessionValidation } from '../utils/apiErrorHandler';

/**
 * Platform statistics response type
 */
export interface PlatformStats {
  totalTasks: number;
  totalTasksPublished: number;
  tasksWaitingReview: number;
  tasksRejected: number;
  tasksApproved: number;
  totalQuestionnaires: number;
  totalQuestionnaireCompleted: number;
  questionnairesWaitingReview: number;
  questionnairesRejected: number;
  questionnairesApproved: number;
  totalTeams: number;
  teamsWaitingReview: number;
  teamsRejected: number;
  teamsApproved: number;
  totalPreReleaseProjects: number;
  preReleaseWaitingReview: number;
  preReleaseRejected: number;
  preReleaseApproved: number;
  activeUsers: number;
  pointsEarned: number;
  aiIdeasGenerated: number;
  aiPreReleaseProjects: number;
  aiLaunchAssets: number;
  totalAIGenerations: number;
}

/**
 * Platform stats API response
 */
interface PlatformStatsResponse {
  success: boolean;
  data: PlatformStats;
  processingTime: number;
  timestamp: string;
}

/**
 * Service for fetching platform statistics via edge function
 */
export class PlatformStatsService {
  private static readonly FUNCTION_NAME = 'platform-stats';
  private static readonly CACHE_KEY = 'platform_stats_cache';
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get cached stats if valid
   */
  private static getFromCache(): PlatformStats | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid
      if (now - timestamp < this.CACHE_DURATION) {
        console.log('📊 Using cached platform stats');
        return data;
      }

      // Cache expired, remove it
      localStorage.removeItem(this.CACHE_KEY);
      return null;
    } catch (error) {
      console.error('Error reading stats cache:', error);
      return null;
    }
  }

  /**
   * Save stats to cache
   */
  private static saveToCache(stats: PlatformStats): void {
    try {
      const cacheData = {
        data: stats,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving stats to cache:', error);
    }
  }

  /**
   * Fetch global platform statistics using edge function
   * This uses the service_role key on the backend to bypass RLS
   */
  static async fetchPlatformStats(): Promise<PlatformStats> {
    // Check cache first
    const cached = this.getFromCache();
    if (cached) return cached;

    return withSessionValidation(async () => {
      // Get current user session for authentication
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error('User not authenticated');
      }

      // Call the edge function with GET method
      const { data, error } = await supabase.functions.invoke<PlatformStatsResponse>(
        this.FUNCTION_NAME,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`
          }
        }
      );

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (!data || !data.success) {
        throw new Error('Invalid response from platform stats function');
      }

      console.log(`📊 Platform stats fetched in ${data.processingTime}ms`);

      // Cache the result
      this.saveToCache(data.data);

      return data.data;
    }, 'PlatformStatsService.fetchPlatformStats');
  }

  /**
   * Fetch platform stats with fallback to default values
   * Use this when you want to ensure stats are always returned
   */
  static async fetchPlatformStatsWithFallback(countriesSupported: number = 0): Promise<PlatformStats & { countriesSupported: number }> {
    try {
      const stats = await this.fetchPlatformStats();
      return {
        ...stats,
        countriesSupported
      };
    } catch (error) {
      console.error('Using fallback stats due to error:', error);

      // Return fallback stats
      return {
        totalTasks: 0,
        totalTasksPublished: 0,
        tasksWaitingReview: 0,
        tasksRejected: 0,
        tasksApproved: 0,
        totalQuestionnaires: 0,
        totalQuestionnaireCompleted: 0,
        questionnairesWaitingReview: 0,
        questionnairesRejected: 0,
        questionnairesApproved: 0,
        totalTeams: 0,
        teamsWaitingReview: 0,
        teamsRejected: 0,
        teamsApproved: 0,
        totalPreReleaseProjects: 0,
        preReleaseWaitingReview: 0,
        preReleaseRejected: 0,
        preReleaseApproved: 0,
        activeUsers: 0,
        pointsEarned: 0,
        countriesSupported,
        aiIdeasGenerated: 0,
        aiPreReleaseProjects: 0,
        aiLaunchAssets: 0,
        totalAIGenerations: 0
      };
    }
  }
}
